import { useState, useCallback } from "react"
import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/* Checklist data                                                      */
/* ------------------------------------------------------------------ */

interface ChecklistItem {
  id: string
  label: string
  description: string
}

interface ChecklistSection {
  title: string
  items: ChecklistItem[]
}

const SECTIONS: ChecklistSection[] = [
  {
    title: "Pre-marketing preparation",
    items: [
      { id: "declutter", label: "Declutter and depersonalise", description: "Remove excess furniture, personal photos, and clutter to help buyers visualise themselves in the space" },
      { id: "clean", label: "Deep clean the property", description: "Professional-level clean including windows, carpets, and all surfaces" },
      { id: "repairs", label: "Complete minor repairs", description: "Fix dripping taps, squeaky doors, cracked tiles, and scuffed paintwork" },
      { id: "garden", label: "Tidy the garden and entrance", description: "Mow the lawn, trim hedges, clear paths, and make the front door welcoming" },
      { id: "staging", label: "Consider staging key rooms", description: "Fresh flowers, neutral throws, and coordinated accessories in living areas and bedrooms" },
    ],
  },
  {
    title: "Essential marketing assets",
    items: [
      { id: "photography", label: "Professional photography", description: "Wide-angle shots of every room, exterior, garden, and any standout features" },
      { id: "floorplans", label: "Measured floorplans", description: "Accurate drawings showing room dimensions and layout, essential for portal listings" },
      { id: "epc", label: "Energy Performance Certificate", description: "Required by law before marketing. Book early as it can take 1-2 weeks" },
      { id: "description", label: "Compelling property description", description: "Highlight key features, lifestyle benefits, and local area. Avoid jargon and cliches" },
      { id: "video", label: "Property video", description: "Short-form video for social media and portals. 2x more enquiries on listings with video" },
    ],
  },
  {
    title: "Online presence",
    items: [
      { id: "rightmove", label: "List on Rightmove", description: "The UK's largest property portal with 86% of buyer traffic" },
      { id: "zoopla", label: "List on Zoopla", description: "The UK's second-largest portal, essential for maximum reach" },
      { id: "onthemarket", label: "List on OnTheMarket", description: "Growing third portal, increasingly popular with vendors" },
      { id: "website", label: "Feature on agency website", description: "Dedicated listing page with all assets, virtual tour, and enquiry form" },
    ],
  },
  {
    title: "Social media and video",
    items: [
      { id: "instagram", label: "Post to Instagram (Reels and feed)", description: "Instagram Reels get 22% more engagement than static posts for property content" },
      { id: "facebook", label: "Share on Facebook", description: "Target local buyer groups and your agency's followers" },
      { id: "tiktok", label: "Create a TikTok", description: "Short-form property tours reach younger buyers and first-time purchasers" },
      { id: "youtube", label: "Upload to YouTube", description: "YouTube Shorts for reach, full-length tours for serious buyers" },
    ],
  },
  {
    title: "Physical marketing",
    items: [
      { id: "board", label: "Install a for-sale board", description: "Generates local awareness and signals the property is available" },
      { id: "brochure", label: "Print property brochure", description: "Leave at viewings and distribute to nearby properties" },
      { id: "leaflets", label: "Leaflet drop to neighbours", description: "Neighbours often know someone looking to move into the area" },
    ],
  },
  {
    title: "Viewings and follow-up",
    items: [
      { id: "viewing-prep", label: "Prepare the property for viewings", description: "Lights on, curtains open, fresh scent, heating comfortable, pets away" },
      { id: "accompanied", label: "Accompany all viewings", description: "Accompanied viewings convert better than unaccompanied. The agent can sell the lifestyle" },
      { id: "feedback", label: "Provide viewing feedback within 24 hours", description: "Fast feedback keeps vendors informed and demonstrates professionalism" },
      { id: "follow-up", label: "Follow up with interested buyers", description: "A prompt call after a viewing can convert interest into an offer" },
    ],
  },
]

/* ------------------------------------------------------------------ */
/* FAQ                                                                 */
/* ------------------------------------------------------------------ */

const FAQ = [
  {
    q: "What is the most important part of property marketing?",
    a: "Photography and online presence are the foundation since 95% of buyers start their search online. But video is increasingly the differentiator: properties with video receive 2x more enquiries. The best marketing packages combine professional photography, video, and targeted social media.",
  },
  {
    q: "How long should marketing take to prepare?",
    a: "Allow 1-2 weeks from instruction to going live. This gives time for an EPC (if needed), professional photography, video creation, floorplans, and property description writing. Rushing the marketing leads to lower-quality assets and fewer enquiries.",
  },
  {
    q: "Should every property get a video?",
    a: "Yes. Video is no longer a premium add-on; it's becoming expected. With tools like QuiteRight, creating a professional listing video takes under 60 seconds and costs a fraction of hiring a videographer. The return in extra viewings far outweighs the minimal cost.",
  },
  {
    q: "What makes a good property description?",
    a: "Focus on lifestyle benefits, not just features. Instead of 'three-bedroom semi-detached', try 'a light-filled family home on a quiet, tree-lined street with excellent schools nearby'. Be honest, highlight what makes the property special, and avoid estate agent cliches like 'deceptively spacious' or 'must be seen'.",
  },
]

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function PropertyMarketingChecklist({ meta }: { meta: SeoMeta }) {
  const [checked, setChecked] = useState<Set<string>>(new Set())

  const toggle = useCallback((id: string) => {
    setChecked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const totalItems = SECTIONS.reduce((sum, s) => sum + s.items.length, 0)
  const completedCount = checked.size
  const progressPercent = totalItems > 0 ? (completedCount / totalItems) * 100 : 0

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "Property Marketing Checklist for Estate Agents",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-22",
            dateModified: "2026-02-22",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "Property Marketing Checklist for Estate Agents",
            description: meta.description,
            step: SECTIONS.flatMap((section) =>
              section.items.map((item) => ({
                "@type": "HowToStep",
                name: item.label,
                text: item.description,
              }))
            ),
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Guides", path: "/guides" }, { name: "Property Marketing Checklist" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Property marketing checklist
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            The complete checklist for marketing a property listing. From
            preparation and photography to video, social media, and viewings.
            Tick items off as you go.
          </p>
        </div>
      </section>

      <SocialShareBar />

      {/* Progress bar + checklist */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-3xl">
          {/* Progress */}
          <div className="sticky top-16 z-10 -mx-6 border-b border-stone bg-white/90 px-6 py-4 backdrop-blur-sm">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-charcoal">
                {completedCount} of {totalItems} completed
              </span>
              <span className="font-display text-lg font-bold text-red">
                {Math.round(progressPercent)}%
              </span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-stone">
              <div
                className="h-full rounded-full bg-red transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Sections */}
          <div className="mt-8 space-y-10">
            {SECTIONS.map((section) => {
              const sectionComplete = section.items.every((item) =>
                checked.has(item.id)
              )
              return (
                <div key={section.title}>
                  <h2 className="flex items-center gap-2 font-display text-xl font-bold text-charcoal">
                    {section.title}
                    <span className="text-red">.</span>
                    {sectionComplete && (
                      <svg
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="size-5 text-green-500"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </h2>
                  <div className="mt-4 space-y-2">
                    {section.items.map((item) => {
                      const isChecked = checked.has(item.id)
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => toggle(item.id)}
                          className={`flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-all ${
                            isChecked
                              ? "border-green-200 bg-green-50/50"
                              : "border-stone bg-white hover:border-stone/80"
                          }`}
                        >
                          <div
                            className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded border transition-all ${
                              isChecked
                                ? "border-green-500 bg-green-500"
                                : "border-warm-grey"
                            }`}
                          >
                            {isChecked && (
                              <svg
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                className="size-3.5 text-white"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </div>
                          <div>
                            <p
                              className={`text-sm font-medium transition-all ${
                                isChecked
                                  ? "text-green-800 line-through"
                                  : "text-charcoal"
                              }`}
                            >
                              {item.label}
                            </p>
                            <p className="mt-0.5 text-xs text-slate">
                              {item.description}
                            </p>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "95%", label: "of buyers start their search online" },
            { stat: "2x", label: "more enquiries on listings with video" },
            { stat: "73%", label: "of vendors prefer agents offering video" },
            { stat: "<60s", label: "to create a listing video with QuiteRight" },
          ].map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">
                {item.label}
              </p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related guides<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              { href: "/guides/winning-instructions", title: "Winning instructions", desc: "Strategies for converting market appraisals into instructions." },
              { href: "/guides/property-video-marketing", title: "Property video marketing", desc: "The definitive guide to video marketing for estate agents." },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Read guide
                  <svg viewBox="0 0 20 20" fill="currentColor" className="size-3.5 transition-transform group-hover:translate-x-0.5">
                    <path fillRule="evenodd" d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z" clipRule="evenodd" />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>
          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg viewBox="0 0 20 20" fill="currentColor" className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180">
                    <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Tick off video in 60 seconds
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            QuiteRight creates professional listing videos from your photos
            in under a minute. One less thing on the checklist. More
            enquiries in your inbox.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
          <p className="mt-5 text-sm text-warm-grey">
            14-day free trial &middot; No credit card &middot; Cancel any time
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
